import { Delete } from "@mui/icons-material"
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid2,
  IconButton,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import Camera from "components/Camera/Camera"

import { Device, DeviceEut, DeviceSetup } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { isDeviceAux, isDeviceEut } from "store/typeGuards"

type AddEutCardProps = {
  newDevice: DeviceEut
  newDeviceSetup: DeviceSetup
  setNewDeviceSetup: (arg: DeviceSetup) => void
  projectDevices: Device[]
}

export const AddEutCard = (props: AddEutCardProps) => {
  const { newDevice, newDeviceSetup, setNewDeviceSetup, projectDevices } = props

  const [connectWithTE, setConnectWithTE] = useState(false)
  const [teBarcode, setTeBarcode] = useState<string>("")
  const [note, setNote] = useState<string>("")
  const [isNotInList, setIsNotInList] = useState(false)

  const { projectID } = useParams()
  const projectsState = useStoreState((state) => state.projects)
  const testEquipmentState = useStoreState((state) => state.testEquipment)
  const updateProject = useStoreActions((actions) => actions.updateProject)

  const { fetchTestEquipment } = useStoreActions((actions) => actions)
  useEffect(() => {
    const loadTestEquipment = async () => {
      try {
        await fetchTestEquipment({ fresh: false })
      } catch (err) {
        console.error("Error loading test equipment:", err)
      }
    }
    loadTestEquipment()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCancelConnectWithTE = () => {
    setConnectWithTE(false)
    setNote("")
    setTeBarcode("")
    setIsNotInList(false)
  }

  const handleSubmitConnectWithTE = () => {
    if (!teBarcode) {
      enqueueSnackbar("Bitte zunächst ein Device scannen oder eingeben.", {
        variant: "warning",
      })
      return
    }

    let addDevice = projectDevices.find((item) => item.barcode === teBarcode)
    if (!addDevice) {
      addDevice = testEquipmentState.find((item) => item.barcode === teBarcode)
    }

    if (!addDevice) {
      enqueueSnackbar("Das gescannte Device konnte nicht gefunden werden.", {
        variant: "error",
      })
      return
    }

    const alreadyLinked = newDeviceSetup.linkedTo?.some(
      (link) => link?.device.barcode === addDevice?.barcode
    )
    if (alreadyLinked) {
      enqueueSnackbar("Bereits hinzugefügt!", {
        variant: "info",
      })
      return
    }

    const sanitizedExisting = (newDeviceSetup.linkedTo ?? []).filter(
      (link): link is NonNullable<typeof link> => !!link
    )

    setNewDeviceSetup({
      ...newDeviceSetup,
      linkedTo: [...sanitizedExisting, { device: addDevice, note: note }],
    })

    if (isNotInList && addDevice) {
      const project = projectsState.find((project) => projectID === project.id)
      if (project) {
        updateProject({
          ...project,
          devices: [...project.devices, addDevice],
        })
      }
    }

    enqueueSnackbar("Erfolgreich verknüpft.", {
      variant: "success",
    })

    handleCancelConnectWithTE()
  }

  const handleRemoveLinked = (barcode: string) => {
    if (!newDeviceSetup) return
    setNewDeviceSetup({
      device: newDevice,
      linkedTo: (newDeviceSetup.linkedTo || []).filter((l) => !(l && l.device.barcode === barcode)),
      images: newDeviceSetup.images || [],
      note: newDeviceSetup.note || "",
    })
  }

  const handleChangeLinkedNote = (barcode: string, newLinkedNote: string) => {
    if (!newDeviceSetup) return
    setNewDeviceSetup({
      device: newDevice,
      linkedTo: (newDeviceSetup.linkedTo || []).map((l) =>
        l && l.device.barcode === barcode ? { device: l.device, note: newLinkedNote } : l
      ),
      images: newDeviceSetup.images || [],
      note: newDeviceSetup.note || "",
    })
  }

  const handleQRCodeScan = (barcode: string) => {
    setIsNotInList(false)

    let addDevice = projectDevices.find((item) => item.barcode === barcode)
    if (!addDevice) {
      addDevice = testEquipmentState.find((item) => item.barcode === barcode)
      setIsNotInList(true)
    }

    if (!addDevice) {
      enqueueSnackbar("Das ist kein TE oder Consumable", {
        variant: "warning",
      })
      setTeBarcode("")
      return
    }

    if ((addDevice && isDeviceAux(addDevice)) || (addDevice && isDeviceEut(addDevice))) {
      enqueueSnackbar("Das ist kein TE oder Consumable", {
        variant: "warning",
      })
      setTeBarcode("")
      return
    }

    if (
      newDeviceSetup &&
      newDeviceSetup.linkedTo?.some((item) => item?.device.barcode === barcode)
    ) {
      enqueueSnackbar("Bereits hinzugefügt!", {
        variant: "warning",
      })
      setTeBarcode("")
      return
    }

    setTeBarcode(barcode)
  }

  return (
    <Card>
      <CardHeader
        title={`${newDevice.barcode}: ${newDevice.name}`}
        slotProps={{ title: { variant: "body1" } }}
        sx={{ p: 0, pl: 1, pt: 1 }}
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {!connectWithTE && (
          <StyledButton onClick={() => setConnectWithTE(true)}>Mit TE verbinden?</StyledButton>
        )}
        {connectWithTE && (
          <>
            <Camera label="Scan QR-Code" onQRCodeScan={handleQRCodeScan} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  label="TE Barcode"
                  name="barcode"
                  size="small"
                  value={teBarcode}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const target = e.target as HTMLInputElement
                      handleQRCodeScan(target.value)
                    }
                  }}
                  onChange={(event) => setTeBarcode(event.currentTarget.value)}
                />
              </Box>

              {teBarcode !== "" && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    label="Kanal / Notiz"
                    name="note"
                    size="small"
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                  />
                </Box>
              )}
              {isNotInList && (
                <Alert severity="warning">
                  Das gescannte Device ist KEIN geplanter Bestandteil der Prüfung. Soll es dennoch
                  hinzugefügt werden?
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1 }}>
                <StyledButton buttonType="cancel" onClick={handleCancelConnectWithTE} fullWidth />
                <StyledButton buttonType="save" onClick={handleSubmitConnectWithTE} fullWidth />
              </Box>
            </Box>
          </>
        )}
        {newDeviceSetup.linkedTo?.map((item) => {
          if (item) {
            return (
              <Box key={item.device.id}>
                <Grid2 container>
                  <Grid2 size={4}>
                    <Typography variant="body2">
                      {item.device.barcode}: {item.device.name}
                    </Typography>
                  </Grid2>
                  <Grid2 size={7}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Kanal / Notiz"
                      value={item.note || ""}
                      onChange={(e) => handleChangeLinkedNote(item.device.barcode, e.target.value)}
                    />
                  </Grid2>
                  <Grid2 size={1}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveLinked(item.device.barcode)}
                        aria-label="Link entfernen"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
            )
          }
          return null
        })}
      </CardContent>
    </Card>
  )
}
